import { Injectable, Injector } from '@angular/core';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseService } from '../../shared/services/base.service';
import { environment } from 'src/environments/environment';
import { IPageApiResponse, IPageResponse } from '../../pliny/interfaces/IPageResponse';

export interface IWorkItem {
  id?: string;
  tenantId?: number;
  name?: string;
  key?: string;
  workItemTypeId?: string;
  workGoalId?: string;
  workQueueId?: string;
  projectNumber?: string;
  businessEntity?: string;
  businessActivity?: string;
  businessEntityIdentifier?: string;
  descriptionMD?: string;
  source?: string;
  priority?: string;
  dueOnUTC?: string;
  assignedTo?: string;
  status?: string;
  decisionOptions?: string;
  referenceNumber?: string;
  referenceType?: string;
  isComplete?: boolean;
  caseRecordTypeId?: string;
  caseRecordId?: string;
  caseNumber?: string;
  // Additional display properties
  workQueueName?: string;
  createdOn?: string;
  [key: string]: any;
}

/**
 * Filter request parameters for WorkItems/GetAll
 * Based on swagger.json - /api/services/app/WorkItems/GetAll
 */
export interface IWorkItemFilterRequest {
  /** General filter text */
  filter?: string | null;
  /** Filter by work item ID (UUID) */
  idFilter?: string | null;
  /** Filter by name */
  nameFilter?: string | null;
  /** Filter by assigned to */
  assignedToFilter?: string | null;
  /** Filter by status */
  statusFilter?: string | null;
  /** Filter by case number */
  caseNumberFilter?: string | null;
  /** Filter by business entity */
  businessEntityFilter?: string | null;
  /** Filter by business activity */
  businessActivityFilter?: string | null;
  /** Filter by work item type ID (UUID) - use this to filter by Task/Approval/Query */
  workItemTypeIdFilter?: string | null;
  /** Filter by work queue ID (UUID) */
  TaskQueueIdFilter?: string | null;
  /** Filter by case record type ID (UUID) */
  caseRecordTypeIdFilter?: string | null;
  /** Filter by business entity identifier */
  businessEntityIdentifierFilter?: string | null;
  /** Sorting - e.g., "name asc" or "createdOn desc" */
  sorting?: string | null;
  /** Number of records to skip (pagination) */
  skipCount?: number;
  /** Maximum number of records to return */
  maxResultCount?: number;
}

/** Work Item Type name for UI display */
export type WorkItemTypeName = 'Task' | 'Approval' | 'Query';

/**
 * Work Item Type DTO
 * Based on swagger.json - WorkItemTypeDto
 */
export interface IWorkItemTypeDto {
  id: string;  // UUID
  name: string;  // Task, Approval, Query, etc.
  prefixCode?: string;  // TSK, APR, QRY, etc.
  status?: string;
}

/**
 * DTO for creating or editing a Work Item Query
 * Based on swagger.json - CreateOrEditWorkItemQueryDto
 */
export interface ICreateOrEditWorkItemQueryDto {
  id?: string;
  tenantId?: number;
  workItemId?: string;  // UUID - the work item this query belongs to
  displayOrder?: number;
  category?: string;
  question?: string;
  questionBy?: string;
  questionOn?: string;  // date-time
  criticalness?: number;
  priority?: number;
  answer?: string;
  answerBy?: string;
  answerOn?: string;  // date-time
  voteUpCount?: number;
  voteDownCount?: number;
  status?: string;
}

/**
 * DTO for creating or editing a Work Item Status
 * Based on swagger.json - CreateOrEditWorkItemStatusDto
 */
export interface ICreateOrEditWorkItemStatusDto {
  id?: string;
  tenantId?: number;
  workItemId: string;
  status?: string;
  statusOnUTC?: string;
  statusBy?: string;
}

/**
 * DTO for creating or editing a Work Item
 * Based on swagger.json - CreateOrEditWorkItemDto
 */
export interface ICreateOrEditWorkItemDto {
  id?: string;
  tenantId?: number;
  name: string;  // Required, 3-100 chars
  key?: string;
  workItemTypeId: string;  // Required UUID
  workGoalId?: string;
  workQueueId: string;  // Required UUID
  projectNumber?: string;
  businessEntity?: string;
  businessActivity?: string;
  businessEntityIdentifier?: string;
  descriptionMD?: string;
  source?: string;
  priority?: string;
  dueOnUTC?: string;
  assignedTo?: string;
  status?: string;
  decisionOptions?: string;
  referenceNumber?: string;
  referenceType?: string;
  isComplete?: boolean;
  caseRecordTypeId?: string;
  caseRecordId?: string;
  caseNumber?: string;
}

/**
 * DTO for creating or editing a Work Item Action (Work Log)
 * Based on swagger.json - CreateOrEditWorkItemActionDto
 */
export interface ICreateOrEditWorkItemActionDto {
  id?: string;
  tenantId?: number;
  workItemId?: string;  // UUID
  actionBy?: string;
  actionOn?: string;  // date-time
  decision?: string;
  decisionReason?: string;
  formActionData?: string;
}

/**
 * DTO for creating or editing a Work Item Work Log
 * Based on swagger.json - CreateOrEditWorkItemWorkLogDto
 */
export interface ICreateOrEditWorkItemWorkLogDto {
  id?: string;
  tenantId?: number;
  workItemId: string;  // Required UUID
  loggedBy?: string;  // Max 1024 chars
  loggedOnUTC: string;  // Required date-time
  workStartedOnUTC?: string;  // Optional date-time
  workEndedOnUTC?: string;  // Optional date-time
  totalMinutes: number;  // Required int64
  description?: string;
}

/**
 * DTO for creating or editing a Work Item Note
 * Based on swagger.json - CreateOrEditWorkItemNoteDto
 */
export interface ICreateOrEditWorkItemNoteDto {
  id?: string;
  tenantId?: number;
  workItemId?: string;  // UUID
  notesMD?: string;
  notesOnUTC?: string;  // date-time
  isPrivate?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class BusinessCenterService extends BaseService {
  
  constructor(injector: Injector) {
    super(injector);
  }

  /**
   * API base URL - points to Document API
   * environment.API_Document = 'https://documents-api.openbots-dev.io/api/services/app'
   */
  override get apiUrl(): string {
    return `${environment.API_Document}`;
  }

  // ============== Work Items Methods ==============

  /**
   * Get Work Items with filter and pagination
   * GET /api/services/app/WorkItems/GetAll
   * 
   * @param filterRequest Filter parameters matching swagger spec
   * @returns Observable with paginated work items
   */
  getWorkItems(filterRequest: IWorkItemFilterRequest): Observable<IPageResponse<IWorkItem>> {
    const params = new URLSearchParams();
    
    // Build query string from filter request - matching swagger parameters exactly
    if (filterRequest.filter) params.append('Filter', filterRequest.filter);
    if (filterRequest.idFilter) params.append('IdFilter', filterRequest.idFilter);
    if (filterRequest.nameFilter) params.append('NameFilter', filterRequest.nameFilter);
    if (filterRequest.assignedToFilter) params.append('AssignedToFilter', filterRequest.assignedToFilter);
    if (filterRequest.statusFilter) params.append('StatusFilter', filterRequest.statusFilter);
    if (filterRequest.caseNumberFilter) params.append('CaseNumberFilter', filterRequest.caseNumberFilter);
    if (filterRequest.businessEntityFilter) params.append('BusinessEntityFilter', filterRequest.businessEntityFilter);
    if (filterRequest.businessActivityFilter) params.append('BusinessActivityFilter', filterRequest.businessActivityFilter);
    if (filterRequest.workItemTypeIdFilter) params.append('WorkItemTypeIdFilter', filterRequest.workItemTypeIdFilter);
    if (filterRequest.TaskQueueIdFilter) params.append('TaskQueueIdFilter', filterRequest.TaskQueueIdFilter);
    if (filterRequest.caseRecordTypeIdFilter) params.append('CaseRecordTypeIdFilter', filterRequest.caseRecordTypeIdFilter);
    if (filterRequest.businessEntityIdentifierFilter) params.append('BusinessEntityIdentifierFilter', filterRequest.businessEntityIdentifierFilter);
    if (filterRequest.sorting) params.append('Sorting', filterRequest.sorting);
    if (filterRequest.skipCount !== undefined) params.append('SkipCount', filterRequest.skipCount.toString());
    if (filterRequest.maxResultCount !== undefined) params.append('MaxResultCount', filterRequest.maxResultCount.toString());
    
    const queryString = params.toString() ? `?${params.toString()}` : '';
    
    return this.get(`/WorkItems/GetAll${queryString}`)
      .pipe(
        map((response: any) => {
          // Handle ABP response structure: { result: { items: [...], totalCount: N } }
          if (response?.result) {
            return {
              totalCount: response.result.totalCount || 0,
              items: (response.result.items || []).map((item: any) => item.workItem || item)
            };
          }
          
          // Handle direct response structure
          if (response?.items && Array.isArray(response.items)) {
            return {
              totalCount: response.totalCount || response.items.length,
              items: response.items.map((item: any) => item.workItem || item)
            };
          }
          
          // Fallback
          return {
            totalCount: 0,
            items: []
          };
        })
      );
  }

  // ============== Work Item Types Methods ==============

  /**
   * Get all Work Item Types (Task, Approval, Query, etc.)
   * GET /api/services/app/WorkItemTypes/GetAll
   * 
   * @returns Observable with work item types list
   */
  getWorkItemTypes(): Observable<IWorkItemTypeDto[]> {
    return this.get(`/WorkItemTypes/GetAll?MaxResultCount=100`)
      .pipe(
        map((response: any) => {
          if (response?.result?.items) {
            return response.result.items.map((item: any) => item.workItemType || item);
          }
          return [];
        })
      );
  }

  /**
   * Get Work Item Types filtered by type name (Task, Approval, Query, etc.)
   * GET /api/services/app/WorkItemTypes/GetAll
   * 
   * @param type Filter by work item type name (e.g., 'Task', 'Approval', 'Query')
   * @returns Observable with work item types list
   */
  getWorkItemTypesByType(type?: 'Task' | 'Approval' | 'Query'): Observable<IWorkItemTypeDto[]> {
    let query = '?MaxResultCount=100';
    if (type) {
      // Filter by name field
      query += `&Filter=${encodeURIComponent(type)}`;
    }
    return this.get(`/WorkItemTypes/GetAll${query}`)
      .pipe(
        map((response: any) => {
          if (response?.result?.items) {
            const items = response.result.items.map((item: any) => item.workItemType || item);
            // Further filter by name if type is provided
            if (type) {
              return items.filter((item: IWorkItemTypeDto) => 
                item.name?.toLowerCase() === type.toLowerCase()
              );
            }
            return items;
          }
          return [];
        })
      );
  }

  /**
   * Get Work Item Type ID by name
   * Helper method to find the UUID for a work item type name (Task, Approval, Query)
   * 
   * @param typeName The work item type name (e.g., 'Task', 'Approval', 'Query')
   * @returns Observable with the work item type ID (UUID) or null if not found
   */
  getWorkItemTypeIdByName(typeName: WorkItemTypeName): Observable<string | null> {
    return this.getWorkItemTypes().pipe(
      map((types: IWorkItemTypeDto[]) => {
        const found = types.find(t => t.name?.toLowerCase() === typeName.toLowerCase());
        return found?.id || null;
      })
    );
  }

  /**
   * Get Work Item by ID for viewing
   * GET /api/services/app/WorkItems/GetWorkItemForView?id={uuid}
   * 
   * @param id Work item ID (UUID)
   * @returns Observable with work item details
   */
  getWorkItemById(id: string): Observable<IWorkItem> {
    return this.get(`/WorkItems/GetWorkItemForView?id=${id}`)
      .pipe(
        map((response: any) => {
          // Handle ABP response structure
          if (response?.result?.workItem) {
            return response.result.workItem;
          }
          if (response?.result) {
            return response.result;
          }
          return response;
        })
      );
  }

  /**
   * Get Work Item for editing
   * GET /api/services/app/WorkItems/GetWorkItemForEdit?Id={uuid}
   * 
   * @param id Work item ID (UUID)
   * @returns Observable with work item edit data
   */
  getWorkItemForEdit(id: string): Observable<any> {
    return this.get(`/WorkItems/GetWorkItemForEdit?Id=${id}`)
      .pipe(
        map((response: any) => {
          if (response?.result?.workItem) {
            return response.result.workItem;
          }
          if (response?.result) {
            return response.result;
          }
          return response;
        })
      );
  }

  /**
   * Create a new Work Item
   * POST /api/services/app/WorkItems/CreateOrEdit
   * 
   * @param workItem Work item data to create
   * @returns Observable with created work item
   */
  createWorkItem(workItem: ICreateOrEditWorkItemDto): Observable<any> {
    return this.post(`/WorkItems/CreateOrEdit`, workItem);
  }

  /**
   * Update an existing Work Item
   * POST /api/services/app/WorkItems/CreateOrEdit
   * 
   * @param id Work item ID to update
   * @param workItem Work item data to update
   * @returns Observable with updated work item
   */
  updateWorkItem(id: string, workItem: ICreateOrEditWorkItemDto): Observable<any> {
    return this.post(`/WorkItems/CreateOrEdit`, { ...workItem, id });
  }

  /**
   * Delete a Work Item
   * DELETE /api/services/app/WorkItems/Delete?Id={uuid}
   * 
   * @param id Work item ID to delete
   * @returns Observable with delete result
   */
  deleteWorkItem(id: string): Observable<any> {
    return this.delete(`/WorkItems/Delete?Id=${id}`);
  }

  // ============== Work Item Actions (Work Logs) Methods ==============

  /**
   * Get Work Item Actions (Work Logs) for a Work Item
   * GET /api/services/app/WorkItemActions/GetAll?WorkItemIdFilter={uuid}
   * 
   * @param workItemId Work item ID (UUID)
   * @returns Observable with work actions response
   */
  getWorkItemWorkLogs(workItemId: string): Observable<any> {
    return this.get(`/WorkItemWorkLogs/GetAll?WorkItemIdFilter=${workItemId}&MaxResultCount=1000`)
      .pipe(
        map((response: any) => {
          let items: any[] = [];
          let totalCount: number = 0;

          // Handle ABP response structure: { result: { items: [...], totalCount: number } }
          if (response?.result) {
            items = response.result.items || [];
            totalCount = response.result.totalCount || 0;
          } else {
            // Handle direct response structure: { items: [...], totalCount: number }
            items = response?.items || [];
            totalCount = response?.totalCount || items.length;
          }

          // Extract workItemWorkLog from each item if nested
          const extractedItems = items.map((item: any) => {
            // If item has workItemWorkLog property, extract it, otherwise use item directly
            return item.workItemWorkLog || item;
          });

          return {
            items: extractedItems,
            totalCount: totalCount
          };
        })
      );
  }

  /**
   * Create Work Item Action (Work Log) - Legacy API
   * POST /api/services/app/WorkItemActions/CreateOrEdit
   * 
   * @param workLogData Work action data to create
   * @returns Observable with created work action
   */
  createWorkItemWorkLog(workLogData: ICreateOrEditWorkItemActionDto): Observable<any> {
    return this.post(`/WorkItemActions/CreateOrEdit`, workLogData);
  }

  /**
   * Create or Edit Work Item Work Log
   * POST /api/services/app/WorkItemWorkLogs/CreateOrEdit
   * 
   * @param workLogData Work log data to create or edit
   * @returns Observable with created/updated work log
   */
  createOrEditWorkItemWorkLog(workLogData: ICreateOrEditWorkItemWorkLogDto): Observable<any> {
    return this.post(`/WorkItemWorkLogs/CreateOrEdit`, workLogData);
  }

  /**
   * Delete Work Item Action (Work Log)
   * DELETE /api/services/app/WorkItemActions/Delete?Id={uuid}
   * 
   * @param workLogId Work action ID to delete
   * @returns Observable with delete result
   */
  deleteWorkItemWorkLog(workLogId: string): Observable<any> {
    return this.delete(`/WorkItemWorkLogs/Delete?Id=${workLogId}`);
  }

  // ============== Work Item Notes (Comments) Methods ==============

  /**
   * Get Work Item Notes for a Work Item
   * GET /api/services/app/WorkItemNotes/GetAll?WorkItemIdFilter={uuid}
   * 
   * @param workItemId Work item ID (UUID)
   * @param sorting Optional sorting parameter (e.g., "notesOnUTC desc")
   * @returns Observable with notes list
   */
  getWorkItemNotes(workItemId: string, sorting?: string): Observable<any> {
    let url = `/WorkItemNotes/GetAll?WorkItemIdFilter=${workItemId}&MaxResultCount=1000`;
    if (sorting) {
      url += `&Sorting=${encodeURIComponent(sorting)}`;
    }
    return this.get(url)
      .pipe(
        map((response: any) => {
          if (response?.result?.items) {
            const items = response.result.items.map((item: any) => item.workItemNote || item);
            return {
              items: items,
              totalCount: response.result.totalCount ?? items.length
            };
          }
          return { items: [], totalCount: 0 };
        })
      );
  }

  /**
   * Create Work Item Note
   * POST /api/services/app/WorkItemNotes/CreateOrEdit
   * 
   * @param noteData Note data to create
   * @returns Observable with created note
   */
  createWorkItemNote(noteData: ICreateOrEditWorkItemNoteDto): Observable<any> {
    return this.post(`/WorkItemNotes/CreateOrEdit`, noteData);
  }

  /**
   * Delete Work Item Note
   * DELETE /api/services/app/WorkItemNotes/Delete?Id={uuid}
   * 
   * @param noteId Note ID to delete
   * @returns Observable with delete result
   */
  deleteWorkItemNote(noteId: string): Observable<any> {
    return this.delete(`/WorkItemNotes/Delete?Id=${noteId}`);
  }

  // ============== Work Item Attachments Methods ==============

  /**
   * Get Attachments for a Work Item
   * GET /api/services/app/WorkItemAttachments/GetAttachments?id={uuid}
   * 
   * @param workItemId Work item ID (UUID)
   * @returns Observable with attachments list
   */
  getWorkItemAttachments(workItemId: string): Observable<any[]> {
    return this.get(`/WorkItemAttachments/GetAttachments?id=${workItemId}`)
      .pipe(
        map((response: any) => {
          if (response?.result) {
            return Array.isArray(response.result) ? response.result : [];
          }
          return Array.isArray(response) ? response : [];
        })
      );
  }

  /**
   * Upload Attachment to a Work Item
   * POST /api/services/app/WorkItemAttachments/CreateWithFiles
   * Content-Type: multipart/form-data
   * 
   * @param workItemId Work item ID
   * @param file File to upload
   * @returns Observable with upload result
   */
  uploadWorkItemAttachment(workItemId: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('workItemId', workItemId);
    formData.append('file', file, file.name);
    formData.append('FileName', file.name);
    formData.append('Name', file.name);
    formData.append('ContentType', file.type);
    formData.append('Length', file.size.toString());
    
    return this.post(`/WorkItemAttachments/CreateWithFiles`, formData);
  }

  /**
   * Download Attachment
   * GET /api/services/app/WorkItemAttachments/GetDownloadFile?id={uuid}
   * 
   * @param attachmentId Attachment ID to download
   * @returns Observable with blob data
   */
  downloadWorkItemAttachment(attachmentId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/WorkItemAttachments/GetDownloadFile?id=${attachmentId}`, {
      responseType: 'blob'
    });
  }

  /**
   * Delete Attachment
   * DELETE /api/services/app/WorkItemAttachments/DeleteFile?Id={uuid}
   * 
   * @param attachmentId Attachment ID to delete
   * @returns Observable with delete result
   */
  deleteWorkItemAttachment(attachmentId: string): Observable<any> {
    return this.delete(`/WorkItemAttachments/DeleteFile?Id=${attachmentId}`);
  }

  // ============== Work Item Queries Methods ==============

  /**
   * Get Work Item Queries for a Work Item
   * GET /api/services/app/WorkItemQueries/GetAll?WorkItemIdFilter={uuid}
   * 
   * @param workItemId Work item ID (UUID)
   * @returns Observable with work item queries list
   */
  getWorkItemQueries(workItemId: string): Observable<any[]> {
    return this.get(`/WorkItemQueries/GetAll?WorkItemIdFilter=${workItemId}&MaxResultCount=1000`)
      .pipe(
        map((response: any) => {
          if (response?.result?.items) {
            return response.result.items.map((item: any) => item.workItemQuery || item);
          }
          return [];
        })
      );
  }

  /**
   * Get All Work Item Queries with optional filters
   * GET /api/services/app/WorkItemQueries/GetAll
   * 
   * @param filters Optional filter parameters
   * @returns Observable with work item queries list
   */
  getAllWorkItemQueries(filters?: {
    filter?: string;
    questionByFilter?: string;
    answerByFilter?: string;
    statusFilter?: string;
    workItemIdFilter?: string;
    maxResultCount?: number;
  }): Observable<any[]> {
    const params = new URLSearchParams();
    
    if (filters?.filter) params.append('Filter', filters.filter);
    if (filters?.questionByFilter) params.append('QuestionByFilter', filters.questionByFilter);
    if (filters?.answerByFilter) params.append('AnswerByFilter', filters.answerByFilter);
    if (filters?.statusFilter) params.append('StatusFilter', filters.statusFilter);
    if (filters?.workItemIdFilter) params.append('WorkItemIdFilter', filters.workItemIdFilter);
    if (filters?.maxResultCount) params.append('MaxResultCount', filters.maxResultCount.toString());
    
    const queryString = params.toString() ? `?${params.toString()}` : '?MaxResultCount=1000';
    
    return this.get(`/WorkItemQueries/GetAll${queryString}`)
      .pipe(
        map((response: any) => {
          if (response?.result?.items) {
            return response.result.items.map((item: any) => item.workItemQuery || item);
          }
          return [];
        })
      );
  }

  /**
   * Create or Edit Work Item Query
   * POST /api/services/app/WorkItemQueries/CreateOrEdit
   * 
   * @param queryData Work item query data
   * @returns Observable with created/updated query
   */
  createOrEditWorkItemQuery(queryData: ICreateOrEditWorkItemQueryDto): Observable<any> {
    return this.post(`/WorkItemQueries/CreateOrEdit`, queryData);
  }

  /**
   * Delete Work Item Query
   * DELETE /api/services/app/WorkItemQueries/Delete?Id={uuid}
   * 
   * @param queryId Query ID to delete
   * @returns Observable with delete result
   */
  deleteWorkItemQuery(queryId: string): Observable<any> {
    return this.delete(`/WorkItemQueries/Delete?Id=${queryId}`);
  }

  /**
   * Get Work Items by Case Number
   * Uses WorkItems/GetAll with CaseNumberFilter
   * 
   * @param caseNumber Case number to filter by
   * @returns Observable with work items for the case
   */
  getWorkItemsByCaseNumber(caseNumber: string): Observable<IWorkItem[]> {
    return this.get(`/WorkItems/GetAll?CaseNumberFilter=${encodeURIComponent(caseNumber)}&MaxResultCount=1000`)
      .pipe(
        map((response: any) => {
          if (response?.result?.items) {
            return response.result.items.map((item: any) => item.workItem || item);
          }
          return [];
        })
      );
  }

  /**
   * Get All Work Item Queries for a Case (by case number)
   * First gets all work items for the case, then gets queries for each work item
   * 
   * @param caseNumber Case number
   * @returns Observable with all queries for the case
   */
  getWorkItemQueriesByCaseNumber(caseNumber: string): Observable<any[]> {
    return this.getWorkItemsByCaseNumber(caseNumber).pipe(
      map((workItems: IWorkItem[]) => {
        // Return work item IDs to fetch queries for
        return workItems.map(wi => wi.id).filter(id => id);
      }),
      // We'll handle the actual query fetching in the component
      // since we need to make multiple API calls
    );
  }

  // ============== Human Tab / Status Methods ==============

  /**
   * Create or Update Work Item (for Human Tab)
   * POST /api/services/app/WorkItems/CreateOrEdit
   * 
   * @param workItemId Work item ID
   * @param updateData Data to update
   * @returns Observable with result
   */
  createOrUpdateWorkItem(workItemId: string, updateData: ICreateOrEditWorkItemDto | any): Observable<any> {
    return this.post(`/WorkItems/CreateOrEdit`, {
      ...updateData,
      id: workItemId
    });
  }

  /**
   * Get Work Item Statuses (for dropdown)
   * GET /api/services/app/WorkItemStatus/GetAll
   * @param workItemIdFilter Optional work item ID to filter by
   */
  getWorkItemStatuses(workItemIdFilter?: string): Observable<{ label: string; value: string }[]> {
    let query = '?MaxResultCount=100';
    if (workItemIdFilter) query += `&WorkItemIdFilter=${workItemIdFilter}`;
    return this.get(`/WorkItemStatus/GetAll${query}`).pipe(
      map((res: any) => {
        const items = res?.result?.items || [];
        const seen = new Set<string>();
        const options: { label: string; value: string }[] = [];
        items.forEach((item: any) => {
          const status = item?.workItemStatus?.status || item?.status;
          if (status && !seen.has(status)) {
            seen.add(status);
            options.push({ label: status, value: status });
          }
        });
        return options.length ? options : [
          { label: 'Created', value: 'Created' },
          { label: 'InProgress', value: 'InProgress' },
          { label: 'Paused', value: 'Paused' },
          { label: 'Completed', value: 'Completed' },
          { label: 'Cancelled', value: 'Cancelled' },
          { label: 'Abandoned', value: 'Abandoned' },
          { label: 'Error', value: 'Error' }
        ];
      })
    );
  }

  /**
   * Create or Edit Work Item Status
   * POST /api/services/app/WorkItemStatus/CreateOrEdit
   */
  createOrEditWorkItemStatus(body: ICreateOrEditWorkItemStatusDto): Observable<any> {
    return this.post(`/WorkItemStatus/CreateOrEdit`, body);
  }

  /**
   * Update Work Item Status (calls WorkItemStatus/CreateOrEdit)
   * @param workItemId Work item ID
   * @param statusData { status, tenantId?, id? } - id is optional for existing status record
   */
  updateWorkItemStatus(workItemId: string, statusData: { status: string; name?: string; tenantId?: number; id?: string }): Observable<any> {
    const body: ICreateOrEditWorkItemStatusDto = {
      workItemId,
      status: statusData.status
    };
    if (statusData.tenantId != null) body.tenantId = statusData.tenantId;
    if (statusData.id) body.id = statusData.id;
    return this.createOrEditWorkItemStatus(body);
  }

  /**
   * Perform Work Item Action (Pause, Start Progress, Complete)
   * Uses WorkItemActions/CreateOrEdit endpoint
   * 
   * @param workItemId Work item ID
   * @param decision Action decision (Pause, Start Progress, Complete)
   * @returns Observable with result
   */
  performWorkItemAction(workItemId: string, decision: string): Observable<any> {
    return this.post(`/WorkItemActions/CreateOrEdit`, {
      workItemId,
      decision
    });
  }

  // ============== Utility Methods ==============

  /**
   * Get status counts from work items
   * Fetches all work items and groups by status
   * 
   * @param workItemTypeId Optional work item type ID (UUID) to filter by
   * @returns Observable with status counts object
   */
  getWorkItemsCountByStatus(workItemTypeId?: string): Observable<{ [status: string]: number }> {
    return this.getWorkItems({ 
      workItemTypeIdFilter: workItemTypeId || undefined,
      maxResultCount: 10000 
    }).pipe(
        map((response) => {
          const items = response.items || [];
          const counts: { [key: string]: number } = {
            'Created': 0,
            'InProgress': 0,
            'Completed': 0,
            'OnHold': 0,
            'Cancelled': 0,
            'Error': 0,
            'Abandoned': 0
          };
          
          items.forEach((item: any) => {
            const status = item.status || 'Created';
            counts[status] = (counts[status] || 0) + 1;
          });
          
          return counts;
        })
      );
  }
}
